import { ReactNode, useState } from "react";
import { Layout } from "./Layout";
import { User, Users, Home, Building2, Calendar, CreditCard, DollarSign, Settings, BarChart3 } from "lucide-react";
import { useLocation, Link } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import {
  Bell,
  LogOut,
  Menu,
  X,
  ChevronDown,
  BookOpen,
  Settings2,
} from "lucide-react";
import { UserRole } from "@/lib/types";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Sidebar } from "@/components/layout/Sidebar";
import logo from '@/assets/staymiddle-logo.png';

interface AdminLayoutProps {
  children: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const { user, logoutMutation } = useAuth();
  const [location] = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Check for admin role instead of specific emails
  if (!user || user.role !== UserRole.ADMIN) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
        <p className="mb-6 text-center text-muted-foreground">
          This area is restricted to Admin access only.
        </p>
        <Link href="/">
          <Button>Return to Home</Button>
        </Link>
      </div>
    );
  }

  const navItems = [
    { label: "Dashboard", href: "/admin/dashboard", icon: <Home className="w-5 h-5 mr-2" />, active: location === "/admin/dashboard" },
    { label: "Users", href: "/admin/users", icon: <Users className="w-5 h-5 mr-2" />, active: location === "/admin/users" },
    { label: "Properties", href: "/admin/properties", icon: <Building2 className="w-5 h-5 mr-2" />, active: location === "/admin/properties" },
    { label: "Bookings", href: "/admin/bookings", icon: <Calendar className="w-5 h-5 mr-2" />, active: location === "/admin/bookings" },
    { label: "Payments", href: "/admin/payments", icon: <CreditCard className="w-5 h-5 mr-2" />, active: location === "/admin/payments" },
    { label: "Revenue", href: "/admin/revenue", icon: <DollarSign className="w-5 h-5 mr-2" />, active: location === "/admin/revenue" },
    { label: "Reports", href: "/admin/reports", icon: <BarChart3 className="w-5 h-5 mr-2" />, active: location === "/admin/reports" },
    { label: "Settings", href: "/admin/settings", icon: <Settings className="w-5 h-5 mr-2" />, active: location === "/admin/settings" },
  ];

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((part) => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  };

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar navItems={navItems} collapsed={collapsed} setCollapsed={setCollapsed} />
      <Layout>{children}</Layout>
    </div>
  );
}
